import { useState, useEffect, useRef, useMemo } from 'react'
import { X, Send, Trash2, Loader2, MessageSquare, AtSign } from 'lucide-react'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import * as api from '../api'
import type { CellComment } from '../api'
import type { User } from '../types'

interface Props {
  vendorId: string
  date: string
  subId: number
  vendorName: string
  amount: number
  onClose: () => void
  onCountChanged?: () => void
}

/** SQLite datetime を "M/D HH:mm" に整形 */
function formatTs(ts: string): string {
  if (!ts) return ''
  const normalized = ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z'
  const d = new Date(normalized)
  if (isNaN(d.getTime())) return ''
  const m = d.getMonth() + 1
  const day = d.getDate()
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${m}/${day} ${hh}:${mm}`
}

/** 本文中の @名前 を青色で装飾（ユーザー存在確認はしない） */
function renderBodyWithMentions(body: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = []
  const re = /@([^\s@]+)/g
  let last = 0
  let m: RegExpExecArray | null
  let key = 0
  while ((m = re.exec(body))) {
    if (m.index > last) parts.push(body.slice(last, m.index))
    parts.push(
      <span key={key++} className="inline-block px-1 mx-0.5 bg-blue-50 text-blue-700 rounded text-[11px] font-medium">
        @{m[1]}
      </span>
    )
    last = m.index + m[0].length
  }
  if (last < body.length) parts.push(body.slice(last))
  return parts
}

export default function CommentDialog({
  vendorId, date, subId, vendorName, amount, onClose, onCountChanged,
}: Props) {
  const currentUser = useAuthStore((s) => s.user)
  const toast = useToastStore()

  const [comments, setComments] = useState<CellComment[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [text, setText] = useState('')
  const [posting, setPosting] = useState(false)
  const [deletingId, setDeletingId] = useState('')
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // 初期読み込み + 未読コメントの自動既読化
  useEffect(() => {
    (async () => {
      setLoading(true)
      try {
        const [cm, us] = await Promise.all([
          api.comments.list(vendorId, date, subId).catch(() => ({ comments: [] })),
          api.usersList.list().catch(() => []),
        ])
        setComments(cm.comments || [])
        setUsers(us || [])
        // 自分の投稿以外で未読のコメントを自動既読化
        const unreadIds = (cm.comments || [])
          .filter((c) => !c.isRead)
          .map((c) => c.id)
        if (unreadIds.length > 0) {
          api.comments.markRead(unreadIds).then(() => {
            // state 上も既読にマーク
            setComments((prev) => prev.map((c) => unreadIds.includes(c.id) ? { ...c, isRead: true } : c))
            onCountChanged?.()
          }).catch(() => { /* 失敗は無視 */ })
        }
      } finally {
        setLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, date, subId])

  // コメント追加後に一番下へスクロール
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [comments])

  // ESCで閉じる
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // @入力でユーザー候補表示
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    setText(val)
    const caret = e.target.selectionStart ?? val.length
    // キャレットの前の単語で @ マッチング
    const before = val.slice(0, caret)
    const m = before.match(/@([^\s@]*)$/)
    setMentionQuery(m ? m[1] : null)
  }

  const mentionCandidates = useMemo(() => {
    if (mentionQuery === null) return []
    const q = mentionQuery.toLowerCase()
    return users
      .filter((u) => u.active)
      .filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .slice(0, 8)
  }, [users, mentionQuery])

  const insertMention = (name: string) => {
    if (!textareaRef.current) return
    const caret = textareaRef.current.selectionStart ?? text.length
    const before = text.slice(0, caret).replace(/@([^\s@]*)$/, `@${name} `)
    const after = text.slice(caret)
    const next = before + after
    setText(next)
    setMentionQuery(null)
    setTimeout(() => {
      textareaRef.current?.focus()
      const pos = before.length
      textareaRef.current?.setSelectionRange(pos, pos)
    }, 0)
  }

  const extractMentionUserIds = (body: string): string[] => {
    const re = /@([^\s@]+)/g
    const found = new Set<string>()
    let m: RegExpExecArray | null
    while ((m = re.exec(body))) {
      const name = m[1]
      const u = users.find((u) => u.name === name || u.email.startsWith(name + '@'))
      if (u) found.add(u.id)
    }
    return Array.from(found)
  }

  const handlePost = async () => {
    const body = text.trim()
    if (!body) { toast.error('コメントを入力してください'); return }
    if (body.length > 2000) { toast.error('2000文字以内で入力してください'); return }
    setPosting(true)
    try {
      const mentions = extractMentionUserIds(body)
      const res = await api.comments.create({ vendorId, date, subId, body, mentions })
      if (res.comment) {
        setComments((prev) => [...prev, res.comment!])
      }
      setText('')
      setMentionQuery(null)
      onCountChanged?.()
    } catch (err) {
      toast.error(`投稿失敗: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setPosting(false)
    }
  }

  const handleDelete = async (c: CellComment) => {
    if (!window.confirm('このコメントを削除しますか？')) return
    setDeletingId(c.id)
    try {
      await api.comments.remove(c.id)
      setComments((prev) => prev.filter((x) => x.id !== c.id))
      onCountChanged?.()
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setDeletingId('')
    }
  }

  const canDelete = (c: CellComment): boolean => {
    if (!currentUser) return false
    return c.userId === currentUser.id || currentUser.role === 'admin'
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="px-4 py-3 border-b border-gray-200 bg-blue-50 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <MessageSquare className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-blue-900 truncate">
                {vendorName}{subId > 0 ? ` (sub${subId})` : ''}
              </h3>
              <div className="text-[11px] text-blue-700 tabular-nums">
                {date} / {amount < 0 ? '−' : ''}¥{Math.abs(amount).toLocaleString()}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 flex-shrink-0"
            title="閉じる (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* コメント一覧 */}
        <div ref={listRef} className="flex-1 overflow-auto p-3 space-y-2 bg-gray-50">
          {loading ? (
            <div className="py-8 text-center text-xs text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin inline" /> 読み込み中...
            </div>
          ) : comments.length === 0 ? (
            <div className="py-8 text-center text-xs text-gray-400">
              まだコメントはありません。下の欄から投稿できます。
            </div>
          ) : (
            comments.map((c) => {
              const isSelf = currentUser && c.userId === currentUser.id
              return (
                <div
                  key={c.id}
                  className={`flex gap-2 ${isSelf ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  <div className={`max-w-[80%] ${isSelf ? 'items-end' : 'items-start'} flex flex-col gap-0.5`}>
                    <div className="text-[10px] text-gray-500 flex items-center gap-2 px-1">
                      <span className="font-medium text-gray-700">{c.userName || '(名無し)'}</span>
                      <span className="tabular-nums">{formatTs(c.createdAt)}</span>
                      {canDelete(c) && (
                        <button
                          type="button"
                          onClick={() => handleDelete(c)}
                          disabled={deletingId === c.id}
                          className="text-red-500 hover:text-red-700 transition"
                          title="削除"
                        >
                          {deletingId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                    <div className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                      isSelf ? 'bg-blue-100 text-blue-900' : 'bg-white text-gray-800 border border-gray-200'
                    }`}>
                      {renderBodyWithMentions(c.body)}
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* 入力フォーム */}
        <div className="relative border-t border-gray-200 p-3 bg-white">
          {/* @メンション候補 */}
          {mentionQuery !== null && mentionCandidates.length > 0 && (
            <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
              <div className="px-2 py-1 text-[10px] text-gray-500 bg-gray-50 border-b border-gray-100 flex items-center gap-1">
                <AtSign className="w-3 h-3" />メンション候補
              </div>
              {mentionCandidates.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => insertMention(u.name)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-blue-50 text-left transition"
                >
                  <span className="font-medium text-gray-800">@{u.name}</span>
                  <span className="text-[10px] text-gray-500 truncate">{u.email}</span>
                  <span className="ml-auto text-[10px] text-gray-400">
                    {u.role === 'admin' ? '管理者' : u.role === 'manager' ? 'マネージャー' : '閲覧者'}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-2 items-end">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleTextChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (e.metaKey || e.ctrlKey) {
                    e.preventDefault()
                    handlePost()
                  }
                  // 通常 Enter は改行（IME 確定Enter問題の対策として、送信は Cmd/Ctrl+Enter のみ）
                }
              }}
              placeholder="コメントを入力... (@で人をメンション / Cmd+Enter で送信)"
              disabled={posting}
              rows={2}
              className="flex-1 min-w-0 px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 resize-y"
              maxLength={2000}
            />
            <button
              type="button"
              onClick={handlePost}
              disabled={posting || !text.trim()}
              className="flex items-center gap-1 px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded transition h-[calc(2rem+2px)]"
            >
              {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              送信
            </button>
          </div>
          <div className="mt-1 flex items-center justify-between text-[10px] text-gray-400">
            <span>@で人をメンション。送信は「送信」ボタン or Cmd/Ctrl+Enter。</span>
            <span className="tabular-nums">{text.length}/2000</span>
          </div>
        </div>
      </div>
    </div>
  )
}
